import { Box, Grid, styled } from '@mui/material';
import {
  CrmJobDTO,
  CrmJobFilterDTO,
  downloadCrmJobs,
  FieldTypeEnum,
  ICustomField,
} from 'wisestep-api';
import { WsColors, WsSelectObj, formattedDateISOWithZeroTime } from 'wisestep-base';
import { object, string } from 'yup';

import { checkIfRequiredField } from '../../../components/BaseCustomField/CustomFieldHelper';
import { downloadHelper } from '../../../features/dataDownload/helper/PayloadHelper';

export interface CrmJobListingResponse {
  content: CrmJobDTO[];
  totalElements: number;
  totalPages: number;
  last: boolean;
  numberOfElements: number;
  first: boolean;
  size: number;
  number: number;
  empty: boolean;
}
export interface ContactFormValues {
  contactList: string[];
}

export const defaultCrmJobFilter: CrmJobFilterDTO = {
  workspaceId: undefined,
  query: '',
  title: '',
  jobStatuses: [],
  jobFunction: '',
  employmentType: '',
  linkedInJobUrl: '',
  location: [],
  clients: [],
  contacts: [],
  jobSource: '',
  isRemote: undefined,
  locations: [],
  includeClosedJobs: false,
  createdByIds: [],
  fromDate: '',
  toDate: '',
  page: 0,
  size: 10,
  sortOrder: '',
  sortBy: '',
  filterByDateType: '',
  customFieldFilters: {},
};

export const mapToCrmJobAdvanceFilter = (data): CrmJobFilterDTO => {
  return {
    page: 0,
    size: 25,
    sortOrder: 'asc',
    sortBy: 'createdOn',
    query: data.query ?? '',
    workspaceId: data.workspaceId,
    title: data.title ?? '',
    jobStatuses: data.jobStatuses ?? [],
    jobFunction: data.jobFunction ?? '',
    employmentType: data.employmentType ?? '',
    linkedInJobUrl: data.linkedInJobUrl ?? '',
    clients: data.clients ?? [],
    contacts: data.contacts ?? [],
    jobSource: data.jobSource ?? '',
    isRemote: data.isRemote ?? false,
    locations: data.locations ?? [],
    includeClosedJobs: data.includeClosedJobs ?? false,
    createdByIds: data.createdByIds ?? [],
    filterByDateType: data.filterByDateType === 'UPDATED_BETWEEN' ? 'modified_on' : 'created_on',
    fromDate: data.fromDate ? formattedDateISOWithZeroTime(data.fromDate) : undefined,
    toDate: data.toDate ? formattedDateISOWithZeroTime(data.toDate) : undefined,
    customFieldFilters: data.customFieldFilters ?? {},
    tags: data.tags ?? [],
    tagsOperator: data.tagsOperator ?? 'is',
  };
};

export const crmJobsTypes: WsSelectObj[] = [
  { label: 'All Jobs', value: 'all_jobs' },
  { label: 'My Jobs', value: 'my_jobs' },
];
export const getAllAvailableColumns = (): string[] => {
  return [
    'title',
    'jobStatus',
    'createdOn',
    'client',
    'employmentType',
    'linkedInJobUrl',
    'jobDescription',
    'postedDate',
    'location',
    'jobSource',
    'jobApplyUrl',
    'totalApplicants',
    'jobClosed',
    'createdBy',
    'modifiedBy',
    'createdByName',
    'modifiedByName',
    'modifiedOn',
    'clientContactCount',
    'eligibleContactCount',
    'taggedContactCount',
    'mailing',
  ];
};

export const getDefaultSelectedColumns = (): string[] => {
  return [
    'title',
    'location',
    'jobStatus',
    'client',
    'clientContactCount',
    'eligibleContactCount',
    'taggedContactCount',
    'createdOn',
  ];
};

export const downloadCrmJobsA = async (
  selectedColumns: string[],
  searchQuery: CrmJobFilterDTO,
  downloadType: string
): Promise<void> => {
  if (!selectedColumns?.length) {
    throw new Error('Please select at least one column to export.');
  }
  if (!['csv', 'xls'].includes(downloadType)) {
    throw new Error('Invalid download type.');
  }
  const exportRequest = {
    ...searchQuery,
    columns: selectedColumns,
  };
  try {
    const response = await downloadCrmJobs(exportRequest, downloadType);
    if (!response?.data) {
      throw new Error('Export failed: No data received from server.');
    }
    downloadHelper(response);
  } catch (error: any) {
    console.error('Error downloading CRM jobs:', error?.message || error);
    throw error;
  }
};

export const linkedInJobRegex = /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.[a-z.]{2,10}\/jobs\/.*$/;

export const getValidationSchema = (defaultFields: ICustomField[]) => {
  return object().shape({
    title: checkIfRequiredField(defaultFields, 'title', true)
      ? string().required('Please provide Job Title').min(1, 'Please provide Job Title')
      : string().nullable(),
    linkedInJobUrl: checkIfRequiredField(defaultFields, 'linkedInJobUrl', true)
      ? string().required('Please provide Job URL').nullable(false)
      : string().nullable(),
    location: (checkIfRequiredField(defaultFields, 'location', true)
      ? object().required('Please provide Hiring Location')
      : object().nullable()
    ).typeError('Please provide Hiring Location'),
    client: checkIfRequiredField(defaultFields, 'client', true)
      ? object().required('Client is required')
      : object(),
    jobDescription: checkIfRequiredField(defaultFields, 'jobDescription', false)
      ? string().required('Please provide Job Description').min(1, 'Please provide Job Description')
      : string(),
  });
};

export const CrmStyledFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 0,
  right: 0,
  justifyContent: 'flex-end',
  width: '49.8vw',
  backgroundColor: WsColors.White,
  borderTop: '1px solid' + WsColors.BorderGrey,
}));

export const periodOptions = [
  {
    label: 'Days',
    value: 'DAYS',
  },
  {
    label: 'Weeks',
    value: 'WEEKS',
  },
  {
    label: 'Months',
    value: 'MONTHS',
  },
  {
    label: 'Years',
    value: 'YEARS',
  },
];

export const HalfPageBottomBlueBar = styled(Box)(({ theme }) => ({
  zIndex: '1299',
  position: 'fixed',
  bottom: '1.5rem',
  textAlign: 'center',
  left: '52%',
  transform: 'translateX(-50%)',
  borderRadius: '0.5rem',
  background: WsColors.TheBlues900,
  padding: '1rem',
  width: '100%',
  maxWidth: 'calc(' + theme.breakpoints.values.xl + 'px - 2rem)',
}));

export type DrawerType =
  | 'eligibleContacts'
  | 'taggedContacts'
  | 'convertToLead'
  | 'bulkTag'
  | 'bulkAssign'
  | null;

export const hardcodedCrmJobFields: ICustomField[] = [
  {
    name: 'title',
    label: 'Job Title',
    type: FieldTypeEnum.TextBox,
    required: true,
  },
  {
    name: 'linkedInJobUrl',
    label: 'Job URL',
    type: FieldTypeEnum.TextBox,
    required: true,
  },
  {
    name: 'location',
    label: 'Hiring Location',
    type: FieldTypeEnum.Location,
    required: true,
  },
  {
    name: 'client',
    label: 'Client',
    type: FieldTypeEnum.Dropdown,
    required: true,
  },
  {
    name: 'jobDescription',
    label: 'Description',
    type: FieldTypeEnum.TextBox,
  },
].map((field) => ({ ...field, isDefault: true }));

export const buildCrmJobFilter = (
  entity: 'client' | 'contact',
  entityId: string
): CrmJobFilterDTO => {
  return {
    ...(entity === 'client'
      ? { clients: [{ id: entityId, name: '' }] }
      : { contacts: [{ id: entityId, firstName: '', emailAddress: '' }] }),
    page: 0,
    size: 50,
    sortBy: 'created_on',
    sortOrder: 'desc',
  };
};
